import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import {
  sumMbcbBudgetReceiptCategories,
  sumMbcbMissionGivingCategories,
} from '../utils/mbcbGivingBreakdown';
import { formatMinistryMonthLabel } from '../utils/mbcbReportPeriodLabels';
import {
  type GuidedSection,
  type MBCBReport,
  type SundayKey,
  type GivingBreakdown,
  type WorshipWeeklyState,
  clearPersistedChat,
  defaultGiving,
  defaultWeekly,
  fetchMyPlanterProfile,
  fetchReport,
  fetchReports,
  mergeGiving,
  mergeWeekly,
  sumWorship,
  avgWorship,
} from '../utils/mbcbReportShared';
import { AiAssistSheet } from '../components/mbcb/AiAssistSheet';
import { EditorShell, StickyNav } from '../components/mbcb/EditorShell';
import { ProgressBar } from '../components/mbcb/ProgressBar';
import { type ReportSummaryData } from '../components/mbcb/ReportSummary';
import { StepGenerosity } from '../components/mbcb/StepGenerosity';
import { StepNumbers } from '../components/mbcb/StepNumbers';
import { StepPeople } from '../components/mbcb/StepPeople';
import { StepReview } from '../components/mbcb/StepReview';
import { StepThanks } from '../components/mbcb/StepThanks';
import { StepWelcome } from '../components/mbcb/StepWelcome';
import { type EditorStep, nextStep, prevStep } from '../components/mbcb/editorSteps';
import { useMbcbAutosave, type SaveStatus } from '../hooks/useMbcbAutosave';

const MBCBReportEditor: React.FC = () => {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const reportId = useMemo(() => {
    const id = parseInt(reportIdParam || '', 10);
    return Number.isFinite(id) ? id : null;
  }, [reportIdParam]);

  const [step, setStep] = useState<EditorStep>('welcome');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [aiSheetOpen, setAiSheetOpen] = useState(false);
  const [aiSection, setAiSection] = useState<GuidedSection>('people');
  const prevReportIdRef = useRef<number | null>(null);

  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [weekly, setWeekly] = useState<WorshipWeeklyState>(defaultWeekly);
  const [giving, setGiving] = useState<GivingBreakdown>(defaultGiving);
  const [newMembers, setNewMembers] = useState(0);
  const [baptisms, setBaptisms] = useState(0);
  const [salvations, setSalvations] = useState(0);
  const [ministryHighlights, setMinistryHighlights] = useState('');
  const [prayerRequests, setPrayerRequests] = useState('');
  const [supportNeeded, setSupportNeeded] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [nextMonthFocus, setNextMonthFocus] = useState('');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [givingNotes, setGivingNotes] = useState('');

  const { data: planter, isLoading: planterLoading } = useQuery({
    queryKey: ['myPlanterProfile'],
    queryFn: fetchMyPlanterProfile,
    retry: false,
  });

  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ['mbcbReports', 'me'],
    queryFn: fetchReports,
    enabled: !!planter?.id,
  });

  const { data: activeReport, isLoading: activeLoading } = useQuery({
    queryKey: ['mbcbReport', reportId],
    queryFn: () => fetchReport(reportId!),
    enabled: !!reportId,
  });

  useEffect(() => {
    if (!reportId || reportsLoading) return;
    if (!reports) return;
    if (!reports.some((r) => r.id === reportId)) {
      navigate('/mbcb', { replace: true });
    }
  }, [reportId, reportsLoading, reports, navigate]);

  const hydrateFromReport = useCallback((r: MBCBReport) => {
    setWeekly(mergeWeekly(r.weeklyMetrics));
    setGiving(mergeGiving(r.givingBreakdown));
    setNewMembers(r.newMembers ?? 0);
    setBaptisms(r.baptisms ?? 0);
    setSalvations(r.salvations ?? 0);
    setMinistryHighlights(r.ministryHighlights || '');
    setPrayerRequests(r.prayerRequests || '');
    setSupportNeeded(r.supportNeeded || '');
    setAdditionalNotes(r.additionalNotes || '');
    setNextMonthFocus(r.nextMonthFocus || '');
    setAttendanceNotes(r.attendanceNotes || '');
    setGivingNotes(r.givingNotes || '');
    setMonth(r.reportMonth);
    setYear(r.reportYear);
  }, []);

  useEffect(() => {
    if (activeReport) hydrateFromReport(activeReport);
  }, [activeReport, hydrateFromReport]);

  const reportingDisabled = planter?.mbcbReportingEnabled === false;

  const canEdit = useMemo(() => {
    if (!activeReport || reportingDisabled) return false;
    return activeReport.status === 'draft' || activeReport.status === 'needs_revision';
  }, [activeReport, reportingDisabled]);

  useEffect(() => {
    if (!reportId || !activeReport) return;
    if (prevReportIdRef.current !== reportId) {
      prevReportIdRef.current = reportId;
      const editable =
        (activeReport.status === 'draft' || activeReport.status === 'needs_revision') &&
        !reportingDisabled;
      setStep(editable ? 'welcome' : 'review');
    }
  }, [reportId, activeReport, reportingDisabled]);

  const buildPayload = useCallback(() => {
    const worshipSum = sumWorship(weekly);
    const incomeTotal = sumMbcbBudgetReceiptCategories(giving.income);
    const missionTotal = sumMbcbMissionGivingCategories(giving.missionGiving);
    return {
      reportMonth: month,
      reportYear: year,
      sundayAttendance: worshipSum,
      smallGroupAttendance: 0,
      newMembers,
      baptisms,
      salvations,
      monthlyIncome: incomeTotal,
      monthlyExpenses: 0,
      buildingFund: 0,
      missionsGiving: missionTotal,
      ministryHighlights: ministryHighlights || null,
      prayerRequests: prayerRequests || null,
      supportNeeded: supportNeeded || null,
      additionalNotes: additionalNotes || null,
      nextMonthFocus: nextMonthFocus || null,
      attendanceNotes: attendanceNotes || null,
      givingNotes: givingNotes || null,
      weeklyMetrics: { worshipAttendance: weekly.worshipAttendance },
      givingBreakdown: {
        ...giving,
        income: { ...giving.income, total: incomeTotal },
        missionGiving: { ...giving.missionGiving, total: missionTotal },
      },
    };
  }, [
    weekly,
    giving,
    month,
    year,
    newMembers,
    baptisms,
    salvations,
    ministryHighlights,
    prayerRequests,
    supportNeeded,
    additionalNotes,
    nextMonthFocus,
    attendanceNotes,
    givingNotes,
  ]);

  const { saveNow, scheduleSave, bindSaveStatus } = useMbcbAutosave({
    reportId,
    enabled: canEdit,
    buildPayload,
  });

  useEffect(() => {
    bindSaveStatus(setSaveStatus);
  }, [bindSaveStatus]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      await apiClient.put(`/mbcb/reports/${reportId}`, buildPayload());
      const res = await apiClient.post(`/mbcb/reports/${reportId}/submit`);
      return res.data.data as MBCBReport;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReport', reportId] });
      if (reportId != null) clearPersistedChat(reportId);
      setStep('thanks');
    },
  });

  const budgetReceiptsTotal = useMemo(
    () => sumMbcbBudgetReceiptCategories(giving.income),
    [giving.income],
  );
  const missionGivingTotal = useMemo(
    () => sumMbcbMissionGivingCategories(giving.missionGiving),
    [giving.missionGiving],
  );

  const summaryData: ReportSummaryData = useMemo(
    () => ({
      ministryHighlights,
      prayerRequests,
      supportNeeded,
      additionalNotes,
      nextMonthFocus,
      attendanceNotes,
      givingNotes,
      weekly,
      giving,
      newMembers,
      baptisms,
      salvations,
      budgetReceiptsTotal,
      missionGivingTotal,
    }),
    [
      ministryHighlights,
      prayerRequests,
      supportNeeded,
      additionalNotes,
      nextMonthFocus,
      attendanceNotes,
      givingNotes,
      weekly,
      giving,
      newMembers,
      baptisms,
      salvations,
      budgetReceiptsTotal,
      missionGivingTotal,
    ],
  );

  const ministryLabel = formatMinistryMonthLabel(month, year);

  const setSunday = (sk: SundayKey, value: string) => {
    const n = parseInt(value, 10);
    setWeekly((prev) => ({
      ...prev,
      worshipAttendance: {
        ...prev.worshipAttendance,
        [sk]: Number.isFinite(n) && n >= 0 ? n : 0,
      },
    }));
  };

  const goBackToList = () => navigate('/mbcb');

  const handleFieldChange = () => scheduleSave();

  const goToStep = async (target: EditorStep) => {
    if (canEdit) await saveNow();
    setStep(target);
  };

  const handleNext = async () => {
    const n = nextStep(step);
    if (!n) return;
    if (canEdit) await saveNow();
    setStep(n);
  };

  const handleBack = async () => {
    const p = prevStep(step);
    if (!p) return;
    if (canEdit && step !== 'welcome') await saveNow();
    setStep(p);
  };

  const openAi = (section: GuidedSection) => {
    setAiSection(section);
    setAiSheetOpen(true);
  };

  const showFooter =
    canEdit && step !== 'welcome' && step !== 'thanks' && step !== 'review';

  const nextLabel =
    step === 'people'
      ? 'Continue to counts'
      : step === 'numbers'
        ? 'Continue to giving'
        : step === 'generosity'
          ? 'Review report'
          : 'Continue';

  if (!reportId || planterLoading || activeLoading || reportsLoading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!activeReport) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center bg-gray-50 px-6">
        <p className="text-gray-600 mb-4">Report not found.</p>
        <button type="button" onClick={goBackToList} className="text-primary-600 font-medium">
          Back to reports
        </button>
      </div>
    );
  }

  return (
    <EditorShell
      month={month}
      year={year}
      saveStatus={saveStatus}
      onBack={goBackToList}
      footer={
        showFooter ? (
          <StickyNav onBack={handleBack} onNext={handleNext} nextLabel={nextLabel} />
        ) : undefined
      }
    >
      {canEdit && step !== 'welcome' && step !== 'thanks' && <ProgressBar active={step} />}

      {!canEdit && step !== 'thanks' && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-sm text-gray-600 shrink-0">
          This report is read-only. Contact staff if you need changes.
        </div>
      )}

      {step === 'welcome' && canEdit && (
        <StepWelcome
          ministryMonth={month}
          ministryYear={year}
          churchName={planter?.churchName}
          onStart={() => setStep('people')}
          needsRevision={activeReport.status === 'needs_revision'}
        />
      )}

      {step === 'people' && (
        <StepPeople
          ministryMonth={month}
          ministryYear={year}
          ministryHighlights={ministryHighlights}
          setMinistryHighlights={setMinistryHighlights}
          prayerRequests={prayerRequests}
          setPrayerRequests={setPrayerRequests}
          supportNeeded={supportNeeded}
          setSupportNeeded={setSupportNeeded}
          canEdit={canEdit}
          onOpenAi={() => openAi('people')}
          reviewNotes={activeReport.reviewNotes}
          showRevisionBanner={activeReport.status === 'needs_revision'}
          onFieldChange={handleFieldChange}
        />
      )}

      {step === 'numbers' && (
        <StepNumbers
          ministryMonth={month}
          ministryYear={year}
          weekly={weekly}
          setSunday={setSunday}
          newMembers={newMembers}
          setNewMembers={setNewMembers}
          baptisms={baptisms}
          setBaptisms={setBaptisms}
          salvations={salvations}
          setSalvations={setSalvations}
          attendanceNotes={attendanceNotes}
          setAttendanceNotes={setAttendanceNotes}
          canEdit={canEdit}
          onFieldChange={handleFieldChange}
        />
      )}

      {step === 'generosity' && (
        <StepGenerosity
          ministryMonth={month}
          ministryYear={year}
          additionalNotes={additionalNotes}
          setAdditionalNotes={setAdditionalNotes}
          givingNotes={givingNotes}
          setGivingNotes={setGivingNotes}
          nextMonthFocus={nextMonthFocus}
          setNextMonthFocus={setNextMonthFocus}
          giving={giving}
          setGiving={setGiving}
          budgetReceiptsTotal={budgetReceiptsTotal}
          missionGivingTotal={missionGivingTotal}
          canEdit={canEdit}
          onOpenAi={() => openAi('generosity')}
          onFieldChange={handleFieldChange}
        />
      )}

      {(step === 'review' || (!canEdit && step !== 'thanks')) && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5">
          <StepReview
            data={summaryData}
            ministryLabel={ministryLabel}
            canEdit={canEdit}
            onEdit={(s) => void goToStep(s)}
            onOpenAi={() => openAi('review')}
            isSubmitting={submitMutation.isPending}
            onSubmit={() => submitMutation.mutate()}
          />
        </div>
      )}

      {step === 'thanks' && (
        <StepThanks
          worshipAverage={Math.round(avgWorship(weekly) * 10) / 10}
          baptisms={baptisms}
          budgetTotal={budgetReceiptsTotal}
          missionTotal={missionGivingTotal}
          onDone={() => {
            if (reportId != null) {
              queryClient.removeQueries({ queryKey: ['mbcbReport', reportId] });
            }
            queryClient.refetchQueries({ queryKey: ['mbcbReports', 'me'] });
            goBackToList();
          }}
        />
      )}

      {canEdit && (
        <AiAssistSheet
          open={aiSheetOpen}
          onClose={() => setAiSheetOpen(false)}
          reportId={reportId}
          section={aiSection}
          canEdit={canEdit}
          buildPayload={buildPayload}
          onReportUpdate={hydrateFromReport}
        />
      )}
    </EditorShell>
  );
};

export default MBCBReportEditor;
